import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Linkedin } from 'lucide-react';
import { useI18n } from '@/i18n/I18nContext';
import { NAV } from '@/constants/testIds';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const LOGO_URL = '/assets/stc-logo.png';
const LINKEDIN_URL = 'https://www.linkedin.com/company/stc-marine-services/';

const linkBase =
  'nav-link text-[12px] uppercase tracking-[0.22em] text-[#F2F5F8]/85 hover:text-[#C9A24E] transition-colors';

export const Navbar = () => {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(() =>
    typeof window !== 'undefined' ? window.scrollY > 8 : false
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  const goContact = (e) => {
    closeMenu();
    if (pathname === '/') {
      e.preventDefault();
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#050D17]/85 border-b border-[#C9A24E]/15'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex h-24 sm:h-28 items-center justify-between">
          <Link
            to="/"
            data-testid={NAV.logoLink}
            className="flex items-center group"
            aria-label="STC Marine Services — Home"
          >
            <img
              src={LOGO_URL}
              alt="STC Marine Services"
              className="h-20 sm:h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              loading="eager"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            <NavLink to="/" data-testid={NAV.homeLink} className={linkBase} end>
              {t('nav.home')}
            </NavLink>
            <NavLink to="/services" data-testid={NAV.servicesLink} className={linkBase}>
              {t('nav.services')}
            </NavLink>
            <a href="/#about" data-testid={NAV.aboutLink} className={linkBase}>
              {t('nav.about')}
            </a>
            <a href="/#why" data-testid={NAV.whyLink} className={linkBase}>
              {t('nav.why')}
            </a>
            <a
              href="/#contact"
              data-testid={NAV.contactLink}
              className={linkBase}
              onClick={goContact}
            >
              {t('nav.contact')}
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={NAV.linkedin}
              aria-label="STC Marine Services on LinkedIn"
              className="inline-flex items-center justify-center h-10 w-10 border border-[#C9A24E]/40 text-[#C9A24E] hover:bg-[#C9A24E]/10 hover:border-[#C9A24E] transition-colors"
            >
              <Linkedin size={16} />
            </a>
            <LanguageSwitcher />
            <a
              href="/#contact"
              data-testid={NAV.ctaContact}
              onClick={goContact}
              className="hidden md:inline-flex items-center px-5 py-2.5 bg-[#C9A24E] text-[#050D17] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#E6CC80] transition-colors"
            >
              {t('nav.cta')}
            </a>
            <button
              type="button"
              data-testid={NAV.mobileToggle}
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center p-2 text-[#F2F5F8] hover:text-[#C9A24E]"
              aria-label="Toggle navigation"
              aria-expanded={open}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid={NAV.mobileMenu}
            className="lg:hidden pb-6 pt-2 border-t border-[#C9A24E]/15"
          >
            <div className="flex flex-col gap-4 pt-4">
              <NavLink to="/" onClick={closeMenu} className="text-sm tracking-wider uppercase text-[#F2F5F8]/85 hover:text-[#C9A24E]" end>
                {t('nav.home')}
              </NavLink>
              <NavLink to="/services" onClick={closeMenu} className="text-sm tracking-wider uppercase text-[#F2F5F8]/85 hover:text-[#C9A24E]">
                {t('nav.services')}
              </NavLink>
              <a href="/#about" onClick={closeMenu} className="text-sm tracking-wider uppercase text-[#F2F5F8]/85 hover:text-[#C9A24E]">
                {t('nav.about')}
              </a>
              <a href="/#why" onClick={closeMenu} className="text-sm tracking-wider uppercase text-[#F2F5F8]/85 hover:text-[#C9A24E]">
                {t('nav.why')}
              </a>
              <a href="/#contact" className="text-sm tracking-wider uppercase text-[#F2F5F8]/85 hover:text-[#C9A24E]" onClick={goContact}>
                {t('nav.contact')}
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="inline-flex items-center gap-2 text-sm tracking-wider uppercase text-[#F2F5F8]/85 hover:text-[#C9A24E]"
              >
                <Linkedin size={14} /> LinkedIn
              </a>
              <a
                href="/#contact"
                onClick={goContact}
                className="mt-2 inline-flex justify-center px-5 py-3 bg-[#C9A24E] text-[#050D17] text-xs uppercase tracking-[0.2em] font-semibold"
              >
                {t('nav.cta')}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
